import deepEqual from 'fast-deep-equal';
import { CreateEducationDetail, DetailFieldLabelType } from '~/types';
import { COUNTRIES, EMPTY_ADDRESS_FORM, STATES } from '../../../constants';
import {
  CreateTrainingType,
  Education,
  EducationDetail,
  Institution,
  Training,
} from '../../../types';
import {
  convertWarningsKeys,
  formatFormAddressForApi,
  hasAddressContent,
  stripLocationSuffix,
  transformCodeToSelectOption,
  transformTypeToOption,
} from '../../utils';
import { DEGREE_OPTIONS, DETAIL_LABELS_OPTIONS, EMPTY_OPTION, TRAINING_TYPES } from './constants';
import {
  DetailFormValues,
  EducationFormValues,
  InstitutionalFormValues,
  TrainingFormValues,
} from './types';

export const institutionApiToForm = (institution: Institution): InstitutionalFormValues => {
  const country = transformCodeToSelectOption(
    institution.address.countryCode,
    COUNTRIES,
    EMPTY_OPTION
  );
  return {
    ...institution,
    name: { value: institution.id.toString(), label: institution.name },
    addressLine1: institution.address.addressLine1 ?? '',
    addressLine2: institution.address.addressLine2 || '',
    city: institution.address.city ?? '',
    state: stateOrProvinceOption(
      institution.address.stateCode || institution.address.province,
      country.value
    ),
    country,
    postalCode: institution.address.postalCode ?? '',
  };
};

export const educationApiToForm = (educationStr: string): EducationFormValues => {
  const education: Education = JSON.parse(educationStr);
  const degreeOption = education.degree
    ? transformCodeToSelectOption(
        education.degree,
        DEGREE_OPTIONS,
        transformTypeToOption(education.degree)
      )
    : EMPTY_OPTION;

  // AI-parsed records may return null for fields the form requires.
  // Explicit guards override spread values for at-risk fields.
  return {
    ...education,
    startDate: education.startDate ?? '',
    graduatedFlag: education.graduatedFlag ?? false,
    details: (education.details ?? []).map((detail) => detailApiToForm(detail as EducationDetail)),
    degree: degreeOption,
    fieldOfStudy: education.fieldOfStudy ?? '',
    explanationTx: education.explanationTx || '',
    endDate: education.endDate || '',
    graduationDate: education.graduationDate || '',
    institution: education.institution
      ? mergeLinkedInstitution(education.institution, education)
      : buildFallbackInstitution(education),
    warnings: convertWarningsKeys(education.warnings),
    validFlag: education.validFlag ?? true,
  };
};

// [MCV-1206] Per-record cached columns on the Education row override the shared Institution relation.
// - Required text fields (address line 1, city, postal code): blank cached values fall through
//   to the relation. A blank override can no longer be saved now that these fields are required.
// - Optional address line 2 keeps an explicit empty-string override as a user-cleared value.
// - Select options (state, country): fall through to the relation when the cached code is empty
//   OR doesn't match a known option. Cached columns may hold legacy 3-char codes (e.g., "USA") or
//   AI-parsed country names (e.g., "Switzerland") that predate the code-list conversion —
//   without this, transformCodeToSelectOption silently renders COUNTRIES[0]/STATES[0].
const matchOptionOrFallback = (
  code: string | null | undefined,
  options: { value: string; label: string }[],
  fallback: { value: string; label: string }
) => {
  if (!code) return fallback;
  return options.find((opt) => opt.value === code) ?? fallback;
};

const requiredCachedAddressOrBase = (cached: string | null | undefined, fallback: string) =>
  cached?.trim() ? cached : fallback;

const stateOrProvinceOption = (code: string | null | undefined, country: string) => {
  if (!code) return EMPTY_OPTION;
  return country === 'US'
    ? matchOptionOrFallback(code, STATES, EMPTY_OPTION)
    : { value: code, label: code };
};

const mergeLinkedInstitution = (
  institution: Institution,
  education: Education
): InstitutionalFormValues => {
  const base = institutionApiToForm(institution);
  const country =
    education.warnings?.['institution.country'] === 'Required' && !education.institutionCountry
      ? EMPTY_OPTION
      : matchOptionOrFallback(education.institutionCountry, COUNTRIES, base.country);
  const cachedState = stateOrProvinceOption(education.institutionStateOrProvince, country.value);
  return {
    ...base,
    addressLine1: requiredCachedAddressOrBase(education.institutionAddressLine1, base.addressLine1),
    addressLine2: education.institutionAddressLine2 ?? base.addressLine2,
    city: requiredCachedAddressOrBase(education.institutionCity, base.city),
    postalCode: requiredCachedAddressOrBase(education.institutionPostalCode, base.postalCode),
    state: cachedState.value ? cachedState : base.state,
    country,
  };
};

const buildFallbackInstitution = (education: Education): InstitutionalFormValues => ({
  name: education.institutionName
    ? { value: 'userAdded', label: education.institutionName }
    : EMPTY_OPTION,
  addressLine1: education.institutionAddressLine1 ?? '',
  addressLine2: education.institutionAddressLine2 ?? '',
  city: education.institutionCity ?? '',
  postalCode: education.institutionPostalCode ?? '',
  state: stateOrProvinceOption(
    education.institutionStateOrProvince,
    education.institutionCountry ?? ''
  ),
  country: transformCodeToSelectOption(education.institutionCountry ?? '', COUNTRIES, EMPTY_OPTION),
});

export const detailApiToForm = (detail: EducationDetail): DetailFormValues => {
  return {
    ...detail,
    _id: detail.id ? detail.id : undefined,
    _education_id: detail.education_id ? detail.education_id : undefined,
    fieldLabel: transformCodeToSelectOption(detail.fieldLabel, DETAIL_LABELS_OPTIONS, EMPTY_OPTION),
  };
};
export const educationsApiToForm = (educationsStr: string): EducationFormValues[] => {
  const educations: Education[] = JSON.parse(educationsStr);
  return educations.map((education) => educationApiToForm(JSON.stringify(education)));
};

export const detailFormValuesToApi = (
  formValues: DetailFormValues
): EducationDetail | CreateEducationDetail => {
  return {
    ...formValues,
    id: formValues._id,
    education_id: formValues._education_id,
    fieldLabel: formValues.fieldLabel.label as DetailFieldLabelType,
  };
};

const optionalDateForApi = (value: string | null | undefined): string | null => {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
};

export const educationFormToApi = (values: EducationFormValues) => {
  const institutionValue = values.institution.name.value;
  const hasLinkedInstitution = institutionValue && institutionValue !== 'userAdded';
  return JSON.stringify({
    startDate: values.startDate,
    endDate: values.endDate,
    degree: values.degree.value,
    fieldOfStudy: values.fieldOfStudy || null,
    details: values.details.map((detail) => detailFormValuesToApi(detail)),
    graduatedFlag: values.graduatedFlag,
    explanationTx: values.explanationTx,
    graduationDate: optionalDateForApi(values.graduationDate),
    institutionId: hasLinkedInstitution ? parseInt(institutionValue) : null,
    institutionName: values.institution.name.label,
    institutionAddressLine1: values.institution.addressLine1,
    institutionAddressLine2: values.institution.addressLine2,
    institutionCity: values.institution.city,
    institutionStateOrProvince: values.institution.state.value,
    institutionCountry: values.institution.country.value,
    institutionPostalCode: values.institution.postalCode,
  });
};

export const errorHandler = (message: string, err?: unknown) => {
  // TODO: MCV-140 - We need a design on how we want to handle errors and how the user is notified.
  console.error(message, err);
};

export const trainingApiToForm = (trainingStr: string): TrainingFormValues => {
  const training: Training = JSON.parse(trainingStr);

  // AI-parsed records may return null for fields the form requires.
  // Explicit guards override spread values for at-risk fields.
  return {
    ...training,
    completedFlag: training.completedFlag ?? false,
    startDate: training.startDate ?? '',
    explanationTx: training.explanationTx ?? '',
    facility: {
      label: training.facility?.name ?? training.facilityName ?? '',
      value: training.facility?.id?.toString() ?? 'userAdded',
      userAdded: training.facility?.userAdded ?? true,
      address: {
        ...(training.facility?.address ?? training.facilityAddress ?? EMPTY_ADDRESS_FORM),
        postalCode: (
          training.facility?.address?.postalCode ??
          training.facilityAddress?.postalCode ??
          training.facilityPostalCode ??
          ''
        ).toString(),
        addressType: { label: 'Permanent', value: 'Permanent' },
        // [MCV-1374] Preserve non-US province codes on load. If the stored
        // stateCode isn't a US state, pass it through as {value, label} rather
        // than collapsing to EMPTY_OPTION -- otherwise a Canada/ON facility
        // round-trips with an empty province.
        stateCode: (() => {
          const code =
            training.facility?.address?.stateCode ??
            training.facilityAddress?.stateCode ??
            training.facilityStateOrProvince ??
            '';
          if (!code) return EMPTY_OPTION;
          return STATES.find((s) => s.value === code) ?? { value: code, label: code };
        })(),
        // [MCV-1374] Legacy training rows may store an empty country OR the
        // historical 3-letter alias "USA" (the training table's prior default
        // before the address column standardized on ISO 3166-1 alpha-2).
        // Before this ticket the form's USCountryField useEffect always wrote
        // US, masking both gaps. Now that the field is a real Select, default
        // empty values to US and normalize "USA" -> "US" so an unrelated edit
        // doesn't silently persist a blank country.
        countryCode: (() => {
          const raw =
            training.facility?.address?.countryCode ??
            training.facilityAddress?.countryCode ??
            training.facilityCountry;
          const normalized = raw === 'USA' ? 'US' : raw;
          if (!normalized) return COUNTRIES.find((c) => c.value === 'US') ?? EMPTY_OPTION;
          return transformCodeToSelectOption(normalized, COUNTRIES, EMPTY_OPTION);
        })(),
      },
    },
    trainingType: training.trainingType
      ? transformCodeToSelectOption(training.trainingType, TRAINING_TYPES, EMPTY_OPTION)
      : EMPTY_OPTION,
    medicalBoard: {
      label: training.medicalBoard?.boardName || '',
      value: training.medicalBoard?.boardName || '',
      id: training.medicalBoard?.id?.toString() || '',
    },
    specialtyBoard: {
      label: training.specialtyBoard?.name || training.specialtyBoardName || '',
      value: training.specialtyBoard?.specialtyBoardCode || 'userAdded',
      id: training.specialtyBoard?.id?.toString() || '',
    },
    specialtyCertification: {
      label: training.specialtyName || training.specialtyCertification?.specialtyName || '',
      value: training.specialtyCertification?.id
        ? training.specialtyCertification.id.toString()
        : 'userAdded',
    },
    subSpecialtyCertification: {
      label:
        training.subspecialtyName ||
        training.subspecialtyCertification?.subspecialtyCertificationName ||
        '',
      value: training.subspecialtyCertification?.id
        ? training.subspecialtyCertification.id.toString()
        : 'userAdded',
    },
    warnings: convertWarningsKeys(training.warnings),
    validFlag: training.validFlag ?? true,
  };
};

export const trainingsApiToForm = (trainingsStr: string): TrainingFormValues[] => {
  const trainings: Training[] = JSON.parse(trainingsStr);
  return trainings.map((training) => trainingApiToForm(JSON.stringify(training)));
};

export const getTrainingCertificationId = (
  option?: { id?: string; value: string } | null
): number | null => {
  const value = option?.id || option?.value;
  if (!value || !/^\d+$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export const trainingFormValuesToApi = (values: Omit<TrainingFormValues, 'id'>) => {
  // Only reuse an unchanged selection/save from the Training wizard. Editing
  // those fields must preserve the submitted address in a separate facility,
  // never silently discard edits or mutate the shared catalog record.
  const facilityId =
    deepEqual(values.facilityReference, values.facility) && /^\d+$/.test(values.facility.value)
      ? Number(values.facility.value)
      : undefined;
  const facilityName =
    values.facility.value &&
    values.facilityReference?.value === values.facility.value &&
    values.facilityReference.label === values.facility.label
      ? (values.facilityReference.rawName ?? values.facility.label)
      : values.facility.label;
  // [MCV-1876] Never persist the picker's "(City, ST)" label suffix as the name.
  // A blank address still carries the default state, which is not saved.
  const facilityNameWithoutLocation = hasAddressContent(values.facility.address)
    ? stripLocationSuffix(
        facilityName,
        values.facility.address?.city,
        values.facility.address?.stateCode?.value
      )
    : facilityName;
  return JSON.stringify({
    completedFlag: values.completedFlag,
    completionDate: values.completionDate || null,
    endDate: values.endDate || null,
    explanationTx: values.explanationTx,
    facilityId,
    facility: {
      name: facilityNameWithoutLocation,
      address: formatFormAddressForApi(
        hasAddressContent(values.facility.address)
          ? { ...EMPTY_ADDRESS_FORM, ...values.facility.address }
          : {
              ...EMPTY_ADDRESS_FORM,
              ...values.facility.address,
              stateCode: { value: '', label: '' },
              countryCode: { value: '', label: '' },
            }
      ),
      userAddedFlag: !!values.facility.userAdded,
    },
    specialtyBoardId:
      values.specialtyBoard.id && values.specialtyBoard.id !== 'userAdded'
        ? parseInt(values.specialtyBoard.id)
        : null,
    specialtyBoardName: values.specialtyBoard.label,
    specialtyCertificationId: getTrainingCertificationId(values.specialtyCertification),
    boardName: values.medicalBoard.label,
    specialtyName: values.specialtyCertification.label,
    startDate: values.startDate,
    subspecialtyCertificationId: getTrainingCertificationId(values.subSpecialtyCertification),
    subspecialtyCertificationName: values.subSpecialtyCertification?.label || null,
    subspecialtyName: values.subSpecialtyCertification?.label || null,
    trainingType: values.trainingType.label,
  } as CreateTrainingType);
};
